import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListSpaceFloors,
  useListSpaceResources,
  useDeleteSpaceResource,
  useGetSpaceOccupancy,
  getListSpaceFloorsQueryKey,
  getListSpaceResourcesQueryKey,
  getGetSpaceOccupancyQueryKey,
} from "@workspace/api-client-react";
import type {
  SpaceFloor,
  SpaceResource,
  OccupancyView,
  CreateSpaceResourceBodyResourceType,
} from "@workspace/api-client-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Trash2, PackagePlus, Activity } from "lucide-react";

// ── Tab routing ───────────────────────────────────────────────────────────────

type TabValue = "resources" | "occupancy";
const TAB_VALUES: readonly TabValue[] = ["resources", "occupancy"];

function tabFromPath(path: string): TabValue {
  const suffix = path.replace(/^\/inventory\/space\/?/, "").split("/")[0] ?? "";
  return (TAB_VALUES as readonly string[]).includes(suffix)
    ? (suffix as TabValue)
    : "resources";
}

// ── Constants ─────────────────────────────────────────────────────────────────
// Phase 54 (SPACE-UX-UNIT-KIND-11): RESOURCE_TYPES (the create-form type
// picker's option list) was removed alongside SpaceResourcesTab's create
// dialog — Space Designer is now the sole author of new resources. Labels
// stay below since the read-only table + occupancy popover still render them.

const RESOURCE_TYPE_LABELS: Record<CreateSpaceResourceBodyResourceType, string> = {
  Workstation: "Workstation",
  Private_Cabin: "Private Cabin",
  Meeting_Room: "Meeting Room",
  Conference_Room: "Conference Room",
  Bay: "Bay",
  Cafeteria: "Cafeteria",
  Reception: "Reception",
  Washroom_Female: "Washroom (Female)",
  Washroom_Male: "Washroom (Male)",
  Store_Room: "Store Room",
  Washbasin: "Washbasin",
  Pantry: "Pantry",
};

const GRID_STEP = 10; // px grid snapping
// Phase A — sensible real-world dimensions in feet per resource type, used when
// creating a new resource and as a render fallback. Eventually sourced from
// space.resourceDefaults (per-space override); inline defaults match the 0044
// backfill so behavior is consistent end-to-end.
const TYPE_DEFAULT_DIMS_FT: Record<string, { widthFt: number; lengthFt: number }> = {
  Workstation:     { widthFt: 3,  lengthFt: 3  },
  Private_Cabin:   { widthFt: 8,  lengthFt: 9  },
  Meeting_Room:    { widthFt: 12, lengthFt: 10 },
  Conference_Room: { widthFt: 16, lengthFt: 10 },
  // Phase C amenity defaults — operator can tweak any room with the override inputs.
  Bay:             { widthFt: 10, lengthFt: 8  },
  Cafeteria:       { widthFt: 20, lengthFt: 15 },
  Reception:       { widthFt: 12, lengthFt: 8  },
  Washroom_Female: { widthFt: 8,  lengthFt: 6  },
  Washroom_Male:   { widthFt: 8,  lengthFt: 6  },
  Store_Room:      { widthFt: 8,  lengthFt: 6  },
  Washbasin:       { widthFt: 2,  lengthFt: 2  },
  Pantry:          { widthFt: 8,  lengthFt: 8  },
};
const DEFAULT_W = 60;
const DEFAULT_H = 60;
const ROOM_DEFAULT_W = 120;
const ROOM_DEFAULT_H = 90;

// ── Helpers ───────────────────────────────────────────────────────────────────

function snap(v: number): number {
  return Math.round(v / GRID_STEP) * GRID_STEP;
}

function isRoom(type: string): boolean {
  // Anything that's "a room or area" (i.e. not an individual workstation seat) —
  // includes Phase C amenities so they get the room-style pixel defaults.
  return (
    type === "Meeting_Room" ||
    type === "Conference_Room" ||
    type === "Private_Cabin" ||
    type === "Bay" ||
    type === "Cafeteria" ||
    type === "Reception" ||
    type === "Washroom_Female" ||
    type === "Washroom_Male" ||
    type === "Store_Room" ||
    type === "Pantry"
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function assignmentTypeBadge(
  assignmentType: string | null | undefined,
): string {
  if (assignmentType === "recurring") return "Recurring";
  if (assignmentType === "daily") return "Day Pass";
  if (assignmentType === "slot") return "Slot";
  return "";
}

// Occupancy color: Free=green, Occupied=red, Out_of_service=gray
function occupancyColor(status: string): string {
  if (status === "Free") return "#22c55e";
  if (status === "Occupied") return "#ef4444";
  if (status === "Out_of_service") return "#9ca3af";
  return "#e5e7eb";
}

function occupancyBgClass(status: string): string {
  if (status === "Free") return "bg-green-100 border-green-400";
  if (status === "Occupied") return "bg-red-100 border-red-400";
  if (status === "Out_of_service") return "bg-gray-100 border-gray-400";
  return "bg-gray-50 border-gray-200";
}

// ── Occupancy Popover ──────────────────────────────────────────────────────────

interface OccupancyPopoverProps {
  resource: OccupancyView | SpaceResource;
  occupancyRow?: OccupancyView;
  onClose: () => void;
  onRefresh: () => void;
}

function OccupancyPopoverContent({
  occupancyRow,
  spaceId,
}: {
  occupancyRow: OccupancyView;
  spaceId?: number | null;
}) {
  // Read-only glance (Phase 54 Slice 2, SPACE-UX-OCC-RO): assignment lives on Add Plan +
  // the Member profile, nowhere else — this popover renders display fields + contextual
  // navigation links only, never a mutation control. (Previously wired the resource-assign
  // and resource-release mutation hooks directly from here — both fully removed.)
  const isOccupied = occupancyRow.status === "Occupied";
  const isFree = occupancyRow.status === "Free";

  return (
    <div className="space-y-3 min-w-[260px]">
      {/* Display-only fields — plain markup per project memory (no FormLabel without FormField) */}
      <div>
        <p className="text-xs text-muted-foreground">Resource</p>
        <p className="font-medium">{occupancyRow.label}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Type</p>
        <p className="text-sm">{RESOURCE_TYPE_LABELS[occupancyRow.resourceType as CreateSpaceResourceBodyResourceType] ?? occupancyRow.resourceType}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Status</p>
        <div className="flex items-center gap-2">
          <Badge
            variant={isOccupied ? "destructive" : occupancyRow.status === "Out_of_service" ? "outline" : "default"}
            className={isFree ? "bg-green-500 text-white" : ""}
          >
            {occupancyRow.status.replace(/_/g, " ")}
          </Badge>
          {occupancyRow.assignmentType && (
            <Badge variant="secondary" className="text-xs">
              {assignmentTypeBadge(occupancyRow.assignmentType)}
            </Badge>
          )}
        </div>
      </div>

      {isOccupied && occupancyRow.memberName && (
        <>
          <div>
            <p className="text-xs text-muted-foreground">Member</p>
            <p className="text-sm font-medium">{occupancyRow.memberName}</p>
          </div>
          {occupancyRow.assignedDate && (
            <div>
              <p className="text-xs text-muted-foreground">Since</p>
              <p className="text-sm">{String(occupancyRow.assignedDate).slice(0, 10)}</p>
            </div>
          )}
          {occupancyRow.releaseDate && (
            <div>
              <p className="text-xs text-muted-foreground">Until</p>
              <p className="text-sm">{String(occupancyRow.releaseDate).slice(0, 10)}</p>
            </div>
          )}
          {occupancyRow.slotStart && (
            <div>
              <p className="text-xs text-muted-foreground">Slot</p>
              <p className="text-sm">{occupancyRow.slotStart} – {occupancyRow.slotEnd ?? "?"}</p>
            </div>
          )}
        </>
      )}

      {/* Contextual navigation, not mutation — assignment lives on Add Plan (Free) /
          the Member profile (Occupied), never in this popover. */}
      {isOccupied && occupancyRow.contactId != null ? (
        <Link
          href={`/members/${occupancyRow.contactId}`}
          className="block border-t pt-2 text-sm text-primary hover:underline"
        >
          View member profile →
        </Link>
      ) : isFree ? (
        <Link
          href={spaceId != null ? `/accounts?spaceId=${spaceId}` : "/accounts"}
          className="block border-t pt-2 text-sm text-primary hover:underline"
        >
          Assign this seat →
        </Link>
      ) : null}
    </div>
  );
}

// ── Resources Tab ─────────────────────────────────────────────────────────────

function SpaceResourcesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: floors = [] } = useListSpaceFloors({
    query: { queryKey: getListSpaceFloorsQueryKey() },
  });
  const { data: resources = [], isLoading } = useListSpaceResources(undefined, {
    query: { queryKey: getListSpaceResourcesQueryKey() },
  });
  const deleteMut = useDeleteSpaceResource();

  const [deleteRow, setDeleteRow] = useState<SpaceResource | null>(null);

  async function handleDelete() {
    if (!deleteRow) return;
    try {
      await deleteMut.mutateAsync({ id: deleteRow.id });
      toast({ title: "Resource deleted." });
      await qc.invalidateQueries({ queryKey: getListSpaceResourcesQueryKey() });
    } catch {
      toast({ title: "Failed to delete resource.", variant: "destructive" });
    }
    setDeleteRow(null);
  }

  const floorMap = new Map(floors.map((f) => [f.id, f]));

  return (
    <div className="space-y-4">
      {/* Phase 54 (SPACE-UX-UNIT-KIND-11): read-only + delete-only. Space
          Designer is now the sole author of new resources — draw a shape in
          the Designer's Library and hit Approve; there is no "New Resource"
          affordance on this tab anymore. Rows here are for audit / cleanup
          only, via the per-row delete action below. */}
      {isLoading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Floor</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No resources yet. Draw shapes in Space Designer and hit Approve to create them.
                  </TableCell>
                </TableRow>
              ) : (
                resources.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.label}</TableCell>
                    <TableCell>{RESOURCE_TYPE_LABELS[r.resourceType as CreateSpaceResourceBodyResourceType] ?? r.resourceType}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {r.capacity} <span className="text-xs">(informational)</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={r.status === "Active" ? "default" : "outline"}
                      >
                        {r.status.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{floorMap.get(r.floorId)?.name ?? `Floor #${r.floorId}`}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteRow(r)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(o) => { if (!o) setDeleteRow(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteRow?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the resource and its assignment history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Occupancy Tab ─────────────────────────────────────────────────────────────

type OccupancyTypeFilter = "all" | "workstations" | "cabins" | "rooms";

function SpaceOccupancyTab() {
  const { data: floors = [] } = useListSpaceFloors({
    query: { queryKey: getListSpaceFloorsQueryKey() },
  });
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [date, setDate] = useState(today());
  const [atTime, setAtTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  });
  const [typeFilter, setTypeFilter] = useState<OccupancyTypeFilter>("all");
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  // auto-select first floor
  useEffect(() => {
    if (floors.length > 0 && selectedFloorId === null) {
      setSelectedFloorId(floors[0].id);
    }
  }, [floors, selectedFloorId]);

  const atParam = `${date}T${atTime}:00`;
  const { data: occupancyRows = [] } = useGetSpaceOccupancy(
    { floorId: selectedFloorId ?? 0, date, at: atParam },
    {
      query: {
        enabled: selectedFloorId !== null,
        queryKey: getGetSpaceOccupancyQueryKey({ floorId: selectedFloorId ?? 0, date, at: atParam }),
      },
    },
  );

  const showTimePicker =
    typeFilter === "rooms" || typeFilter === "all";

  const filteredRows = occupancyRows.filter((row) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "workstations") return row.resourceType === "Workstation";
    if (typeFilter === "cabins") return row.resourceType === "Private_Cabin";
    if (typeFilter === "rooms")
      return (
        row.resourceType === "Meeting_Room" ||
        row.resourceType === "Conference_Room"
      );
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select
          value={selectedFloorId ? String(selectedFloorId) : "__none__"}
          onValueChange={(v) =>
            setSelectedFloorId(v === "__none__" ? null : Number(v))
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select floor" />
          </SelectTrigger>
          <SelectContent>
            {floors.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Label className="text-sm">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-40"
          />
        </div>

        {showTimePicker && (
          <div className="flex items-center gap-2">
            <Label className="text-sm">Time</Label>
            <Input
              type="time"
              value={atTime}
              onChange={(e) => setAtTime(e.target.value)}
              className="w-32"
            />
          </div>
        )}

        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as OccupancyTypeFilter)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="workstations">Workstations</SelectItem>
            <SelectItem value="cabins">Cabins</SelectItem>
            <SelectItem value="rooms">Rooms</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid of resources */}
      {selectedFloorId === null ? (
        <div className="rounded-lg border bg-muted p-8 text-center text-muted-foreground">
          Select a floor to view occupancy.
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-lg border bg-muted p-8 text-center text-muted-foreground">
          No resources for this floor/filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filteredRows.map((row) => (
            <Popover
              key={row.resourceId}
              open={openPopoverId === row.resourceId}
              onOpenChange={(o) =>
                setOpenPopoverId(o ? row.resourceId : null)
              }
            >
              <PopoverTrigger asChild>
                <button
                  className={`rounded-lg border-2 p-3 text-left transition-colors hover:opacity-80 cursor-pointer w-full ${occupancyBgClass(row.status)}`}
                >
                  <div className="font-medium text-sm truncate">{row.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {RESOURCE_TYPE_LABELS[row.resourceType as CreateSpaceResourceBodyResourceType] ?? row.resourceType}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge
                      variant={
                        row.status === "Occupied"
                          ? "destructive"
                          : row.status === "Out_of_service"
                          ? "outline"
                          : "default"
                      }
                      className={`text-xs ${row.status === "Free" ? "bg-green-500 text-white" : ""}`}
                    >
                      {row.status.replace(/_/g, " ")}
                    </Badge>
                    {row.assignmentType && (
                      <Badge variant="secondary" className="text-xs">
                        {assignmentTypeBadge(row.assignmentType)}
                      </Badge>
                    )}
                  </div>
                  {row.memberName && (
                    <div className="text-xs mt-1 truncate text-muted-foreground">
                      {row.memberName}
                    </div>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4" align="start">
                <OccupancyPopoverContent
                  occupancyRow={row}
                  spaceId={floors.find((f) => f.id === selectedFloorId)?.spaceId}
                />
              </PopoverContent>
            </Popover>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InventorySpace() {
  const [location, setLocation] = useLocation();
  const tab = tabFromPath(location);
  const [openCreate, setOpenCreate] = useState(false);

  function handleTabChange(next: string) {
    if (next === tab) return;
    setOpenCreate(false);
    setLocation(`/inventory/space/${next}`);
  }

  return (
    <div className="space-y-4 md:space-y-6 max-w-screen-2xl mx-auto px-2 md:px-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Inventory · Space</h1>
        <p className="text-xs md:text-sm text-muted-foreground">
          Resource management and real-time occupancy. For floor-plan editing, use{" "}
          <Link href="/inventory/space/designer" className="underline hover:text-primary">
            Space Designer
          </Link>
          .
        </p>
      </header>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-2 h-auto md:h-10">
          <TabsTrigger value="resources" className="shrink-0 whitespace-nowrap">
            <PackagePlus className="mr-2 h-4 w-4" />Resources
          </TabsTrigger>
          <TabsTrigger value="occupancy" className="shrink-0 whitespace-nowrap">
            <Activity className="mr-2 h-4 w-4" />Occupancy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resources">
          <SpaceResourcesTab />
        </TabsContent>
        <TabsContent value="occupancy">
          <SpaceOccupancyTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
